import { notEmpty } from "../utils/utils.js";
/** Allium `block_timestamp` wire format: UTC without a timezone suffix. */
const ALLIUM_BLOCK_TIMESTAMP_REGEX = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(\.\d+)?$/;
/**
 * Normalize an Allium `block_timestamp` to ISO-8601 UTC.
 *
 * Allium ships `"YYYY-MM-DD HH:MM:SS"` in UTC. `Date.parse` treats that as
 * local time, which makes trade ages negative for users west of UTC. ClickHouse
 * parses the same field with `parseDateTime64BestEffort(..., 'UTC')` — match
 * that semantics here.
 */
export function parseAlliumBlockTimestampToIsoUtc(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    const alliumMatch = ALLIUM_BLOCK_TIMESTAMP_REGEX.exec(trimmed);
    if (alliumMatch) {
        const iso = `${alliumMatch[1]}T${alliumMatch[2]}${alliumMatch[3] ?? ""}Z`;
        const parsed = new Date(iso);
        if (Number.isNaN(parsed.getTime()))
            return undefined;
        return parsed.toISOString();
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime()))
        return undefined;
    return parsed.toISOString();
}
/**
 * Safely coerce a value to a `Date`.
 *
 * GraphQL `DateTime` scalars arrive as ISO strings in Apollo responses, but our
 * generated TypeScript types declare them as `Date`. Calling `.getTime()` /
 * `.toLocaleString()` directly on such fields crashes at runtime with
 * "getTime is not a function". Wrap any timestamp that comes from a query
 * result with this helper before calling `Date` methods.
 *
 * Accepts `Date`, ISO string, or epoch millis number. Returns `undefined` for
 * `null`/`undefined` input — and also for inputs that parse into an "Invalid
 * Date" (e.g. unparseable strings like `"garbage"`), so callers can use
 * `?? fallback` safely without having to re-check `Number.isNaN(.getTime())`.
 */
export function toDate(value) {
    if (!notEmpty(value))
        return undefined;
    let parsed;
    if (value instanceof Date) {
        parsed = value;
    }
    else if (typeof value === "number") {
        parsed = new Date(value);
    }
    else {
        const alliumIso = parseAlliumBlockTimestampToIsoUtc(value);
        parsed = new Date(alliumIso ?? value);
    }
    if (Number.isNaN(parsed.getTime()))
        return undefined;
    return parsed;
}
